import { useEffect, useState, type ImgHTMLAttributes } from "react";

const TRANSPARENT_PIXEL = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

const loadedUrls = new Set<string>();

export interface SmoothImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "placeholder"> {
  src?: string;
  alt?: string;
  fit?: "contain" | "cover";
}

export const SmoothImage = ({ src, alt = "", fit = "contain", style, ...rest }: SmoothImageProps) => {
  const [bg, setBg] = useState<string>(() => (src && loadedUrls.has(src) ? src : ""));

  useEffect(() => {
    if (!src) {
      setBg("");
      return;
    }
    if (loadedUrls.has(src)) {
      setBg(src);
      return;
    }
    let cancelled = false;
    setBg("");
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      loadedUrls.add(src);
      setBg(src);
    };
    img.onerror = () => {
      if (cancelled) return;
      setBg("");
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <img
      {...rest}
      src={TRANSPARENT_PIXEL}
      alt={alt}
      style={{
        backgroundImage: bg ? `url("${bg}")` : undefined,
        backgroundSize: fit,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        ...style,
      }}
    />
  );
};
